import React, { useState } from 'react';
import { View, TouchableOpacity, Image, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';

import { ON_ACCENT_TEXT, inkOnFill } from '../../styles/accentText';
import { surfaceFill } from '../../styles/platformBackgrounds';

const WEB = Platform.OS === 'web';

/**
 * CORE-1733 (возврат QA 27.10) — заглушка обложки без картинки. Холодные
 * #E5E7EB / #1F2937 выпадали из тёплой палитры v4: на вебе — тёплый тон
 * чуть темнее светлой поверхности и графит с тёплым отливом из её же стопа.
 * Натив — как был.
 */
export const COVER_PLACEHOLDER = WEB
  ? { light: '#EFE2CB', dark: '#37322E' }
  : { light: '#E5E7EB', dark: '#1F2937' };

const LIVE_FILL = '#22C55E';
export interface UpcomingCardRoom {
  id: number;
  title: string;
  startDateTimeUtc: string;
  durationMinutes: number;
  coverImageUrl?: string | null;
  masterName: string;
  participantCount: number;
  participantCap: number;
  isLive: boolean;
  isOwn: boolean;
  /** True when the underlying room belongs to a recurring series (SCRUM-49). */
  isPartOfSeries: boolean;
}

export const formatRelativeRu = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  const today = now.toDateString() === d.toDateString();
  const tomorrow = new Date(now.getTime() + 86_400_000).toDateString() === d.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (today) return `Сегодня · ${hh}:${mm}`;
  if (tomorrow) return `Завтра · ${hh}:${mm}`;
  const day = d.getDate();
  const month = d.toLocaleDateString('ru-RU', { month: 'short' });
  return `${day} ${month} · ${hh}:${mm}`;
};

export const formatParticipantCounter = (count: number, cap: number): string =>
  `${count} / ${cap}`;

export const getUpcomingCtaLabel = (isLive: boolean): string =>
  isLive ? 'ЗАЙТИ' : 'ПРИСОЕДИНИТЬСЯ';

export const getUpcomingBadges = (
  room: Pick<UpcomingCardRoom, 'isLive' | 'isOwn'>,
): Array<'live' | 'own'> => {
  const badges: Array<'live' | 'own'> = [];
  if (room.isLive) badges.push('live');
  if (room.isOwn) badges.push('own');
  return badges;
};

interface Props {
  room: UpcomingCardRoom;
  onPress: () => void;
  /**
   * SCRUM-49 — when provided and `room.isOwn`, the «ВАША» badge becomes
   * a pencil button that opens the owner action sheet (edit / delete /
   * cancel series). Non-owners never see it.
   */
  onOpenOwnerMenu?: () => void;
}

export const UpcomingCard: React.FC<Props> = ({ room, onPress, onOpenOwnerMenu }) => {
  // Lazy requires — see ArchiveCard. Defers the ../ui and ../ShareModal
  // chains (StyleSheet, expo-clipboard) past module load so the helper
  // tests that import this file don't crash before reaching their cases.
  const { Typography } = require('../ui/Typography');
  const { ShareModal } = require('../ShareModal');
  const { buildShareText, shareOrOpenModal } = require('../../utils/roomShare');
  const { isDark, colors } = useTheme();
  const [showShare, setShowShare] = useState(false);
  const badges = getUpcomingBadges(room);
  const showLiveBadge = badges.includes('live');
  const showOwnBadge = badges.includes('own');
  // CORE-743 — without subject/message ShareModal falls back to
  // `${title} — ${url}`, which is yet another wording of the same sentence.
  const share = buildShareText({ type: 'room', id: room.id, title: room.title });
  const shareAnalytics = { entityType: 'room', entityId: room.id, surface: 'practice_room_card' } as const;

  // CORE-1733 (возврат QA 27.10) — на вебе карточка — поверхность v4 (была плоская
  // #FFFFFF / #1A1A1A, календарь рядом уже на v4), подпись — токен темы: #6B7280
  // на поверхности давал 3.9:1. Натив — как был.
  const cardFill = WEB
    ? surfaceFill(isDark ? 'dark' : 'light')
    : { backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' };
  const titleCol = isDark ? '#F5F3F0' : '#0F1419';
  const mutedCol = WEB ? colors.textMuted : (isDark ? 'rgba(255,255,255,0.55)' : '#6B7280');
  const placeholderBg = COVER_PLACEHOLDER[isDark ? 'dark' : 'light'];

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      testID={`upcoming-card-${room.id}`}
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        ...cardFill,
      } as any}
    >
      <View style={{ position: 'relative' } as any}>
        {room.coverImageUrl ? (
          <Image source={{ uri: room.coverImageUrl }} style={{ width: '100%', aspectRatio: 16 / 9 } as any} />
        ) : (
          <View style={{ width: '100%', aspectRatio: 16 / 9, backgroundColor: placeholderBg } as any} />
        )}
        {showLiveBadge && (
          // CORE-1733 (возврат QA 27.10) — белый на зелёном давал 2.28:1, подпись по контрасту.
          <View style={{ position: 'absolute', top: 8, left: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: LIVE_FILL } as any}>
            <Typography variant="captionAccent" style={{ color: inkOnFill(LIVE_FILL) } as any}>LIVE</Typography>
          </View>
        )}
        {showOwnBadge && (
          onOpenOwnerMenu ? (
            <TouchableOpacity
              onPress={(e) => { e.stopPropagation(); onOpenOwnerMenu(); }}
              accessibilityLabel="Настройки моей Live-сессии"
              testID={`upcoming-card-${room.id}-owner-menu`}
              style={{ position: 'absolute', top: 8, right: 8, flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: 'rgba(233, 130, 69,0.92)' } as any}
            >
              <Typography variant="captionAccent" style={{ color: ON_ACCENT_TEXT } as any}>ВАША</Typography>
              <Ionicons name="pencil" size={11} color={ON_ACCENT_TEXT} />
            </TouchableOpacity>
          ) : (
            <View style={{ position: 'absolute', top: 8, right: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: 'rgba(233, 130, 69,0.92)' } as any}>
              <Typography variant="captionAccent" style={{ color: ON_ACCENT_TEXT } as any}>ВАША</Typography>
            </View>
          )
        )}
        <View style={{ position: 'absolute', bottom: 8, right: 8, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, backgroundColor: 'rgba(15,20,25,0.78)', flexDirection: 'row', alignItems: 'center', gap: 4 } as any}>
          <Ionicons name="people" size={11} color="#FFF" />
          <Typography variant="captionAccent" style={{ color: '#FFF' } as any}>
            {formatParticipantCounter(room.participantCount, room.participantCap)}
          </Typography>
        </View>
        <TouchableOpacity
          // CORE-786 — a phone gets the OS sheet, a desktop the popup.
          onPress={(e) => { e.stopPropagation(); void shareOrOpenModal({ url: share.url, title: room.title, subject: share.subject, message: share.message }, shareAnalytics, () => setShowShare(true)); }}
          style={{ position: 'absolute', bottom: 8, left: 8, width: 28, height: 28, borderRadius: 14, backgroundColor: 'rgba(15,20,25,0.78)', alignItems: 'center', justifyContent: 'center' } as any}
          accessibilityLabel="Поделиться"
          testID={`upcoming-card-${room.id}-share`}
        >
          <Ionicons name="share-social-outline" size={15} color="#FFF" />
        </TouchableOpacity>
      </View>
      <View style={{ padding: 10, gap: 4 } as any}>
        <Typography variant="label" numberOfLines={2} style={{ color: titleCol } as any}>
          {room.title}
        </Typography>
        <Typography variant="caption" numberOfLines={1} style={{ color: mutedCol } as any}>
          {formatRelativeRu(room.startDateTimeUtc)} · {room.masterName}
        </Typography>
      </View>
      <ShareModal
        visible={showShare}
        onClose={() => setShowShare(false)}
        url={share.url}
        title={room.title}
        subject={share.subject}
        message={share.message}
        analytics={shareAnalytics}
      />
    </TouchableOpacity>
  );
};
